import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { ChevronDown, ListFilter } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Which part of the menu an item report covers (owner, 2026-10-26: "say I only
 * want beverage no food"). The server filters the rows AND the totals; this only
 * picks the ids.
 *
 * One tree, master group → group → category (owner, 2026-11-02): ticking a level
 * ticks everything under it, and the report covers every ticked category. Only
 * category ids are sent, so a pick never narrows another (the old three lists
 * matched all levels at once and silently dropped picks).
 */

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/reports/filters/menu`;

export const EMPTY_MENU = { master_group: [], group: [], category: [] };

export const menuFilterActive = menu =>
  Boolean(menu && (menu.master_group.length || menu.group.length || menu.category.length));

/** Master groups with their groups and categories; categories outside any group last. */
export function menuTree(levels) {
  const groups = levels?.groups || [];
  const categories = levels?.categories || [];
  const catsOf = groupId => categories.filter(c => c.group_id === groupId);
  const groupNode = g => ({ ...g, categories: catsOf(g.id) });
  const masters = (levels?.master_groups || []).map(m => {
    const kids = groups.filter(g => g.master_group_id === m.id).map(groupNode);
    return { ...m, groups: kids, categoryIds: kids.flatMap(g => g.categories.map(c => c.id)) };
  });
  const masterIds = new Set(masters.map(m => m.id));
  const looseGroups = groups.filter(g => !masterIds.has(g.master_group_id)).map(groupNode);
  const groupIds = new Set(groups.map(g => g.id));
  const looseCategories = categories.filter(c => !groupIds.has(c.group_id));
  return { masters, looseGroups, looseCategories };
}

/** "Beverages", "Bar Menu, Soups", "5 categories" — for the chip and the printed subtitle. */
export function menuFilterLabel(menu, levels) {
  if (!menuFilterActive(menu)) return "";
  const picked = new Set(menu.category);
  const names = [];
  const take = (name, ids) => {
    if (ids.length && ids.every(id => picked.has(id))) {
      names.push(name);
      ids.forEach(id => picked.delete(id));
    }
  };
  const tree = menuTree(levels);
  tree.masters.forEach(m => take(m.name, m.categoryIds));
  [...tree.masters.flatMap(m => m.groups), ...tree.looseGroups]
    .forEach(g => take(g.name, g.categories.map(c => c.id)));
  (levels?.categories || []).forEach(c => { if (picked.has(c.id)) { names.push(c.name); picked.delete(c.id); } });
  const unknown = picked.size;
  if (unknown || names.length > 3) return `${menu.category.length} categories`;
  return names.join(", ");
}

export default function MenuFilterControl({ value, onChange, token, onLevels }) {
  const [open, setOpen] = useState(false);
  const [levels, setLevels] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    axios
      .get(API, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(response => {
        if (!live) return;
        // Lists, whatever came back: a missing level is an empty one, not a crash.
        const data = response?.data || {};
        const list = key => (Array.isArray(data[key]) ? data[key] : []);
        const next = { master_groups: list("master_groups"), groups: list("groups"), categories: list("categories") };
        setLevels(next);
        onLevels?.(next);
      })
      .catch(() => { if (live) setError("Could not load the menu groups"); });
    return () => { live = false; };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const tree = useMemo(() => menuTree(levels), [levels]);
  const picked = useMemo(() => new Set(value.category), [value.category]);

  // Ticking a level ticks (or clears) every category under it.
  const toggle = ids => {
    if (!ids.length) return;
    const all = ids.every(id => picked.has(id));
    const next = new Set(picked);
    ids.forEach(id => (all ? next.delete(id) : next.add(id)));
    onChange({ ...EMPTY_MENU, category: [...next] });
  };

  const label = menuFilterActive(value) ? menuFilterLabel(value, levels) : "All of the menu";

  const row = (key, name, ids, depth, isActive) => {
    const count = ids.filter(id => picked.has(id)).length;
    return (
      <label key={key}
        className={`flex items-center gap-snug py-2 pr-2 text-body cursor-pointer hover:bg-muted/50 ${depth === 0 ? "font-semibold" : ""} ${ids.length ? "" : "opacity-50"}`}
        style={{ paddingLeft: `${0.5 + depth * 1.25}rem` }}>
        <TreeCheckbox
          checked={ids.length > 0 && count === ids.length}
          indeterminate={count > 0 && count < ids.length}
          disabled={!ids.length}
          onChange={() => toggle(ids)}
          testId={`menu-filter-${key}`}
        />
        <span className={`truncate ${isActive === false ? "text-muted-foreground" : ""}`}>
          {name}{isActive === false ? " (off)" : ""}
        </span>
      </label>
    );
  };
  const groupRows = (g, depth) => [
    row(`group-${g.id}`, g.name, g.categories.map(c => c.id), depth, g.is_active),
    ...g.categories.map(c => row(`category-${c.id}`, c.name, [c.id], depth + 1, c.is_active)),
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`h-8 inline-flex items-center gap-2 rounded-md border px-2.5 text-body hover:bg-muted/50 max-w-[22rem] ${menuFilterActive(value) ? "border-primary text-primary bg-primary/5" : "bg-background text-foreground"}`}
          aria-label="Filter by menu"
          data-testid="menu-filter-trigger"
        >
          <ListFilter className="h-4 w-4 shrink-0" />
          <span className="truncate">{label}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" collisionPadding={8}
        className="w-[min(28rem,calc(100vw-16px))] p-3 flex flex-col gap-snug max-h-[var(--radix-popover-content-available-height)]"
        data-density="compact">
        {error ? (
          <p className="text-meta text-danger" role="alert">{error}</p>
        ) : !levels ? (
          <p className="text-meta text-muted-foreground">Loading the menu…</p>
        ) : (
          <>
            <div className="min-h-0 overflow-y-auto overscroll-contain border rounded-md divide-y" data-testid="menu-filter-tree">
              {tree.masters.map(m => [
                row(`master_group-${m.id}`, m.name, m.categoryIds, 0, m.is_active),
                ...m.groups.flatMap(g => groupRows(g, 1)),
              ])}
              {tree.looseGroups.flatMap(g => groupRows(g, 0))}
              {tree.looseCategories.map(c => row(`category-${c.id}`, c.name, [c.id], 0, c.is_active))}
            </div>
            <div className="flex items-center justify-between shrink-0">
              <span className="text-micro text-muted-foreground">Rows and totals cover every ticked category.</span>
              <button type="button" className="text-meta text-primary hover:underline disabled:opacity-50"
                disabled={!menuFilterActive(value)} onClick={() => onChange(EMPTY_MENU)} data-testid="menu-filter-clear">
                All of the menu
              </button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

/** A checkbox that can show "some of this is ticked". */
function TreeCheckbox({ checked, indeterminate, disabled, onChange, testId }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = indeterminate; }, [indeterminate]);
  return (
    <input ref={ref} type="checkbox" className="h-4 w-4 shrink-0" checked={checked} disabled={disabled}
      aria-checked={indeterminate ? "mixed" : checked} onChange={onChange} data-testid={testId} />
  );
}
